/**
 * Signal BYO device-link (plan signal-connector.md §5.1). Creates an account row
 * on mount, fetches the QR once, then polls status until the linked number is
 * claimed.
 *
 * Two things differ from the WhatsApp QR step this mirrors:
 *   - The QR is SINGLE-USE and fetching it opens the link window, so it is fetched
 *     once and only re-fetched on an explicit retry. Progress comes from polling
 *     the status route, never from re-fetching the QR.
 *   - A scan can succeed while the account stays invisible to the API (spike F3 —
 *     signal-cli builds its account map at daemon startup). The server reports
 *     that as `linkHint`, and every hint is surfaced as an actionable state rather
 *     than an endless spinner.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { InfoNote } from '@/components/patterns/info-note';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ApiError, type SignalLinkHint, signalAccountApi } from '@/lib/api';
import { qk } from '@/lib/query-client';

interface Props {
  visibility?: 'workspace' | 'private';
  onLinked: (accountId: string) => void;
  onBack?: () => void;
}

const POLL_INTERVAL_MS = 2_000;

/** Human-facing copy for each server-reported link hint. */
const HINT_COPY: Record<
  Exclude<SignalLinkHint, null>,
  { tone: 'warning' | 'destructive'; title: string; body: string }
> = {
  container_restart_required: {
    tone: 'warning',
    title: 'Almost there — Signal needs a nudge',
    body: "If you've already scanned the code, the link worked but the Signal service hasn't picked it up yet. It needs a restart to finish. Contact your admin, then check again.",
  },
  ambiguous_link: {
    tone: 'warning',
    title: "Couldn't tell which number is yours",
    body: 'More than one Signal account finished linking at the same moment, so Brain stopped rather than risk connecting the wrong one. Wait a few seconds and check again.',
  },
  signal_unavailable: {
    tone: 'destructive',
    title: "Can't reach Signal right now",
    body: 'Brain lost contact with the Signal service. Still trying — your scan is not lost.',
  },
};

export function SignalQrLink({ visibility = 'workspace', onLinked, onBack }: Props) {
  const [accountId, setAccountId] = useState<string | null>(null);
  const [linked, setLinked] = useState(false);

  const createMut = useMutation({
    mutationFn: () => signalAccountApi.create(visibility),
    onSuccess: (res) => setAccountId(res.accountId),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        toast.error("You've linked as many Signal accounts as this workspace allows.");
        return;
      }
      toast.error("Couldn't start a Signal link. Please try again.");
    },
  });

  // Create the account row once, on mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run exactly once
  useEffect(() => {
    createMut.mutate();
  }, []);

  // The QR is single-use — fetch it once per account and never on an interval.
  const {
    data: qrData,
    isError: qrFailed,
    refetch: refetchQr,
    isFetching: qrFetching,
  } = useQuery({
    queryKey: accountId ? [...qk.signalAccount(accountId), 'qr'] : ['signal-qr-idle'],
    queryFn: () => signalAccountApi.qr(accountId as string),
    enabled: !!accountId,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Progress comes from polling status, which claims the number once it appears.
  const { data: account, refetch: refetchStatus } = useQuery({
    queryKey: accountId ? qk.signalAccount(accountId) : ['signal-account-idle'],
    queryFn: () => signalAccountApi.status(accountId as string),
    enabled: !!accountId && !linked,
    refetchInterval: linked ? false : POLL_INTERVAL_MS,
  });

  const isWorking = account?.status === 'working' && !!account.e164;

  useEffect(() => {
    if (isWorking && !linked && accountId) {
      setLinked(true);
      toast.success('Signal connected!');
      onLinked(accountId);
    }
  }, [isWorking, linked, accountId, onLinked]);

  function startOver() {
    setAccountId(null);
    setLinked(false);
    createMut.mutate();
  }

  const hint = account?.linkHint ?? null;
  const hintCopy = hint ? HINT_COPY[hint] : null;
  const createFailed = createMut.isError;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="neutral">
          <Smartphone className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 text-lg font-semibold">Link your Signal</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Open Signal on your phone →{' '}
          <span className="font-medium">Settings → Linked Devices → +</span>, then scan this code.
          Brain joins as a linked device, exactly like Signal Desktop.
        </p>

        {/* R-NOHIST — Signal linked devices receive NO history. Users coming from
            the WhatsApp flow expect backfill and will think this is broken. */}
        <div className="mt-6 rounded-lg border border-info-border bg-info-bg p-4">
          <p className="text-[13.5px] font-semibold text-info">Signal starts from now on</p>
          <p className="mt-1 text-[13px] text-info/80">
            Unlike WhatsApp, a linked Signal device can&apos;t fetch old messages — Brain indexes
            what arrives from the moment you connect. To search older conversations, import a Signal
            backup afterwards.
          </p>
        </div>

        {/* R-PRIV — third-party DM exposure, disclosed before the scan. */}
        <div className="mt-3 rounded-lg border border-border-subtle p-4">
          <p className="text-[13.5px] font-semibold">Before you connect</p>
          <p className="mt-1 text-[13px] text-muted-foreground">
            You choose which chats to index next. Direct messages include people who haven&apos;t
            agreed to this, so Brain keeps every DM private to you — never shared with your
            workspace.
            {visibility === 'private'
              ? ' Groups you index stay private to you too.'
              : ' Groups you index are searchable by your workspace unless you mark them private.'}
          </p>
        </div>

        {/* QR / status */}
        <div className="mt-6 flex flex-col items-center gap-4">
          {createFailed || qrFailed ? (
            <Alert variant="destructive" className="w-full">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Couldn&apos;t get a code</AlertTitle>
              <AlertDescription>
                Brain couldn&apos;t reach Signal to start the link. Start over and try again.
              </AlertDescription>
            </Alert>
          ) : linked ? (
            <div className="flex items-center gap-2 text-success">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Connected — setting things up…</span>
            </div>
          ) : (
            <div className="flex h-[232px] w-[232px] items-center justify-center rounded-lg border border-border-subtle bg-white p-3">
              {qrData?.qr ? (
                <img src={qrData.qr} alt="Signal linking QR code" className="h-full w-full" />
              ) : (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              )}
            </div>
          )}

          {!createFailed && !qrFailed && !linked && !hintCopy && (
            <p className="font-mono text-[12px] uppercase tracking-[0.05em] text-faint">
              {qrData?.qr ? 'Waiting for you to scan…' : 'Preparing your code…'}
            </p>
          )}
        </div>

        {/* Actionable link states — never leave a successful scan spinning (F3). */}
        {hintCopy && !linked && (
          <Alert variant={hintCopy.tone} className="mt-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{hintCopy.title}</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>{hintCopy.body}</p>
              <Button variant="outline" size="sm" onClick={() => void refetchStatus()}>
                <RefreshCw className="h-4 w-4" />
                Check again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {(createFailed || qrFailed) && (
          <Button size="cta" className="mt-6 w-full" onClick={startOver}>
            Start over
          </Button>
        )}

        {/* The code expires once scanned or after a while; re-fetching re-links. */}
        {!createFailed && !qrFailed && !linked && (
          <div className="mt-6 border-t border-border-subtle pt-4">
            <button
              type="button"
              onClick={() => void refetchQr()}
              disabled={qrFetching || !accountId}
              className="text-[13px] text-faint transition-colors hover:text-foreground disabled:opacity-50"
            >
              {qrFetching ? 'Getting a new code…' : 'Code expired? Get a new one'}
            </button>
            <InfoNote className="mt-3">
              Keep this tab open while you scan — Brain finishes the link here.
            </InfoNote>
          </div>
        )}
      </div>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-[13px] text-faint transition-colors hover:text-foreground"
        >
          ← Choose a different way to connect
        </button>
      )}
    </div>
  );
}
